import QRCode from "qrcode";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const diasSemana = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const diasActivosTexto = (diasActivos = []) => {
  const activos = diasActivos
    .map((activo, i) => (activo ? diasSemana[i] : null))
    .filter(Boolean);
  return activos.length > 0 ? activos.join(", ") : "Todos los días";
};

const generarQRDataUrl = async (token) => {
  try {
    return await QRCode.toDataURL(token, {
      errorCorrectionLevel: "H",
      margin: 2,
      width: 600,
    });
  } catch {
    return null;
  }
};

const truncarTexto = (str, max) => {
  if (!str) return "";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};


const tarjetaProceso = ({ proceso, programa, empresa, qrKey, qrSize }) => {
  const reglas   = proceso.reglasProgramacion || {};
  const horarios = reglas.horariosEjecucion   || [];
  const nombreEmpresa = empresa?.nombre || empresa?.razonSocial || "";

  const textoHorario = horarios.length > 0
    ? horarios.map((h) => `${h.horaInicio}-${h.horaFin}`).join(", ")
    : "Sin horario";

  const filaDato = (etiqueta, valor) => ({
    text: [
      { text: `${etiqueta}: `, bold: true, color: "#666666" },
      { text: valor, color: "#333333" },
    ],
    fontSize: 6.5,
    alignment: "center",
    margin: [2, 0, 2, 1],
  });

  return {
    stack: [
      //  Mini banner superior 
      {
        table: {
          widths: ["*"],
          body: [[{
            stack: [
              ...(nombreEmpresa ? [{ text: truncarTexto(nombreEmpresa, 42), fontSize: 6, color: "#bbbbbb", alignment: "center" }] : []),
              { text: truncarTexto(programa.nombre || "—", 36), fontSize: 8, bold: true, color: "#ffffff", alignment: "center" },
            ],
            fillColor: "#2c2c2c",
            margin: [4, 3, 4, 3],
          }]],
        },
        layout: "noBorders",
        margin: [0, 0, 0, 4],
      },

      qrKey
        ? { image: qrKey, width: qrSize, alignment: "center", margin: [0, 2, 0, 4] }
        : {
            text: "QR no disponible",
            alignment: "center",
            color: "#999999",
            fontSize: 8,
            margin: [0, qrSize / 2, 0, qrSize / 2],
          },

      { text: truncarTexto(proceso.nombre, 32), alignment: "center", bold: true, fontSize: 9.5, margin: [2, 0, 2, 1] },
      ...(proceso.descripcion
        ? [{ text: truncarTexto(proceso.descripcion, 48), alignment: "center", fontSize: 6.5, italics: true, color: "#777777", margin: [2, 0, 2, 2] }]
        : []),

      filaDato("Días", truncarTexto(diasActivosTexto(reglas.diasActivos), 40)),
      filaDato("Horario", truncarTexto(textoHorario, 40)),
      ...(reglas.toleranciaRetardo > 0 ? [filaDato("Tolerancia", `${reglas.toleranciaRetardo} min`)] : []),
    ],
    margin: [4, 4, 4, 6],
  };
};

// Arma N tarjetas por hoja en una grilla de 2 columnas (máximo) x M filas
const construirPaginasGrid = ({ procesos, qrImagenes, programa, empresa, qrPorHoja }) => {
  const columnas   = Math.min(2, qrPorHoja);
  const filas      = Math.ceil(qrPorHoja / columnas);
  const anchoUtil  = 552; 
  const altoUtil   = 712; 
  const anchoCelda = anchoUtil / columnas;
  const altoCelda  = altoUtil / filas;
  const qrSize     = Math.max(55, Math.min(anchoCelda - 20, altoCelda - 105));

  const grupos = chunk(procesos, columnas * filas);

  return grupos.map((grupo, idxGrupo) => {
    const celdas = grupo.map((proc) => {
      const procesoId = proc._id?.toString() || proc.id;
      const qrInfo     = qrImagenes[procesoId];
      return tarjetaProceso({ proceso: proc, programa, empresa, qrKey: qrInfo?.key || null, qrSize });
    });

    while (celdas.length < columnas * filas) celdas.push({ text: "" });

    const body = [];
    for (let f = 0; f < filas; f++) {
      body.push(celdas.slice(f * columnas, f * columnas + columnas));
    }

    return {
      table: {
        widths: Array(columnas).fill(anchoCelda),
        body,
      },
      layout: {
        hLineColor: () => "#dddddd",
        vLineColor: () => "#dddddd",
      },
      ...(idxGrupo === grupos.length - 1 ? {} : { pageBreak: "after" }),
    };
  });
};

// Página completa por proceso: logo · info · QR grande · horarios · pie
const paginaProceso = ({ proceso, programa, empresa, qrDataUrl, qrKey, esUltima }) => {
  const reglas   = proceso.reglasProgramacion || {};
  const horarios = reglas.horariosEjecucion   || [];
  const logoKey  = `logo_${programa._id}`;

  const nombreEmpresa = empresa?.nombre || empresa?.razonSocial || "";

  const textosHorarios = horarios.length > 0
    ? horarios.map((h) => `${h.horaInicio} – ${h.horaFin}${h.descripcion ? `  (${h.descripcion})` : ""}`).join("    ")
    : "Sin horario restringido";

  return {
    stack: [
      {
        canvas: [{
          type:  "rect",
          x: -30, y: -40,
          w: 636, h: 90,
          color: "#2c2c2c",
        }],
        margin: [0, 0, 0, 0],
      },

      // ── Logo + empresa (sobre la banda) ──────────────────────────────────
      {
        columns: [
          empresa?.logo?.base64Completa
            ? { image: logoKey, width: 44, margin: [0, -76, 0, 0] }
            : { text: "", width: 44 },
          {
            stack: [
              { text: nombreEmpresa, style: "empresaBanda" },
              { text: programa.nombre || "—", style: "programaBanda" },
            ],
            margin: [10, -74, 0, 0],
          },
          {
            text: programa.fecha ? moment(programa.fecha).format("LL") : "",
            style: "fechaBanda",
            alignment: "right",
            margin: [0, -72, 0, 0],
          },
        ],
      },

      // ── Espacio + nombre del proceso ──────────────────────────────────────
      { text: "", margin: [0, 28, 0, 0] },
      {
        text: proceso.nombre,
        style: "nombreProceso",
        alignment: "center",
        margin: [0, 0, 0, 4],
      },
      ...(proceso.descripcion
        ? [{ text: proceso.descripcion, style: "descripcionProceso", alignment: "center", margin: [0, 0, 0, 0] }]
        : []),

      // ── Línea divisoria ──────────────────────────────────────────────────
      {
        canvas: [{ type: "line", x1: 60, y1: 8, x2: 456, y2: 8, lineWidth: 0.5, lineColor: "#cccccc" }],
        margin: [0, 4, 0, 4],
      },

      // ── QR grande centrado ───────────────────────────────────────────────
      qrDataUrl
        ? {
            image:     qrKey,
            width:     320,
            alignment: "center",
            margin:    [0, 8, 0, 8],
          }
        : {
            text:      "QR no disponible",
            style:     "descripcionProceso",
            alignment: "center",
            color:     "#999999",
            margin:    [0, 40, 0, 40],
          },

      // ── Texto de instrucción ─────────────────────────────────────────────
      {
        text: "Escanea este código con la app BIWO para registrar el proceso",
        style:     "instruccion",
        alignment: "center",
        margin:    [0, 0, 0, 10],
      },

      // ── Línea divisoria ──────────────────────────────────────────────────
      {
        canvas: [{ type: "line", x1: 60, y1: 0, x2: 456, y2: 0, lineWidth: 0.5, lineColor: "#cccccc" }],
        margin: [0, 0, 0, 10],
      },

      // ── Info de horarios y días ──────────────────────────────────────────
      {
        columns: [
          {
            stack: [
              { text: "DÍAS ACTIVOS", style: "etiquetaPie" },
              { text: diasActivosTexto(reglas.diasActivos), style: "valorPie" },
            ],
            alignment: "center",
          },
          {
            stack: [
              { text: "HORARIO DE EJECUCIÓN", style: "etiquetaPie" },
              { text: textosHorarios, style: "valorPie" },
            ],
            alignment: "center",
          },
          ...(reglas.toleranciaRetardo > 0
            ? [{
                stack: [
                  { text: "TOLERANCIA", style: "etiquetaPie" },
                  { text: `${reglas.toleranciaRetardo} min`, style: "valorPie" },
                ],
                alignment: "center",
              }]
            : []),
        ],
        margin: [0, 0, 0, 0],
      },
    ],
    // Salto de página entre procesos
    ...(esUltima ? {} : { pageBreak: "after" }),
  };
};

// Genera la definición pdfmake para el PDF de tarjetas QR de procesos
// datosRaw: { programa, procesos, empresa, tokens: { [procesoId]: string } }
const procesosQrPdf = async (datosRaw) => {
  const programa = datosRaw.programa || {};
  const procesos = datosRaw.procesos || [];
  const empresa  = datosRaw.empresa  || {};
  const tokens   = datosRaw.tokens   || {};

  const images = {};

  if (empresa?.logo?.base64Completa) {
    images[`logo_${programa._id}`] = empresa.logo.base64Completa;
  }

  // Generar imágenes QR en paralelo
  const qrImagenes = {};
  await Promise.all(
    procesos.map(async (proc) => {
      const token = tokens[proc._id?.toString() || proc.id];
      if (!token) return;
      const dataUrl = await generarQRDataUrl(token);
      if (dataUrl) {
        const key = `qr_${proc._id || proc.id}`;
        qrImagenes[proc._id?.toString() || proc.id] = { key, dataUrl };
        images[key] = dataUrl;
      }
    }),
  );

  const qrPorHoja = Number(datosRaw.qrPorHoja) || 1;

  const paginas = qrPorHoja <= 1
    ? procesos.map((proc, idx) => {
        const procesoId = proc._id?.toString() || proc.id;
        const qrInfo    = qrImagenes[procesoId];
        return paginaProceso({
          proceso:   proc,
          programa,
          empresa,
          qrDataUrl: qrInfo?.dataUrl || null,
          qrKey:     qrInfo?.key     || null,
          esUltima:  idx === procesos.length - 1,
        });
      })
    : construirPaginasGrid({ procesos, qrImagenes, programa, empresa, qrPorHoja });

  return {
    info: { title: `Tarjetas QR — ${programa.nombre || "Programa"}` },
    content: paginas,
    ...(Object.keys(images).length > 0 ? { images } : {}),
    styles: {
      empresaBanda:       { fontSize: 9,  color: "#aaaaaa" },
      programaBanda:      { fontSize: 13, bold: true, color: "#ffffff" },
      fechaBanda:         { fontSize: 8,  color: "#aaaaaa" },
      nombreProceso:      { fontSize: 22, bold: true, color: "#1a1a1a" },
      descripcionProceso: { fontSize: 10, color: "#666666" },
      instruccion:        { fontSize: 9,  color: "#888888", italics: true },
      etiquetaPie:        { fontSize: 7,  bold: true, color: "#888888" },
      valorPie:           { fontSize: 9,  color: "#333333" },
    },
    pageSize:    "LETTER",
    pageMargins: [30, 40, 30, 40],
  };
};

export default procesosQrPdf;
